import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Text from '~/components/Text';

import Button from '../Button';
import Input from '../Forms/Input';
import StatusInputs from './StatusInputs';

interface Props {
  emailFieldName?: string;
  emailFieldType?: 'email' | 'text';
  isSubmitting: boolean;
  useMagicLink: boolean;
  useOneTimePassword: boolean;
  showLoginWithPassword?: boolean;
}

const LoginWithPassword = ({
  emailFieldName = 'email',
  emailFieldType = 'email',
  isSubmitting = false,
  useMagicLink = true,
  useOneTimePassword = false,
  showLoginWithPassword = false,
}: Props) => {
  const [sendEmail, setSendEmail] = useState(true);

  const { t } = useTranslation();

  // replacing magic link with OTP email
  const toggleLabel = useMagicLink ? t('login.login_with_email_label') : t('login.login_with_otp_label');
  const actionLabel = useMagicLink ? t('login.send_magic_link_button_label') : t('login.send_otp_button_label');

  const toggleButton = (
    <button type="button" onClick={() => setSendEmail(!sendEmail)}>
      <Text as="span" size="xs" type="body" weight="bold" theme={'default'}>
        {sendEmail ? t('login.login_with_password_label') : toggleLabel}
      </Text>
    </button>
  );

  return (
    <div className="flex flex-col w-full space-y-2 sm:space-y-6">
      <StatusInputs
        name="login"
        errorMessage={`${t('errors.default')} ${t('errors.try_later')}`}
        successMessage={sendEmail ? t('toast.login_link.message') : t('login.success.message')}
      />
      <input type="hidden" name="request_magic_link" value={useMagicLink ? 'true' : 'false'} />
      <input type="hidden" name="use_one_time_password" value={useOneTimePassword ? 'true' : 'false'} />
      <div className="flex flex-col w-full text-left gap-y-6">
        <div className="flex flex-col gap-y-1">
          <Input
            name={emailFieldName}
            type={
              emailFieldType
            } /* type="text" when rendering this component in a modal due to issues with sparkloop integration see PR#997 */
            required
            placeholder={t('login.email_placeholder')}
            autoComplete="email"
            label={t('login.email_label')}
          />
          {sendEmail && showLoginWithPassword ? <div className="flex justify-end w-full">{toggleButton}</div> : null}
        </div>
        {!sendEmail ? (
          <div className="flex flex-col w-full gap-y-1">
            <Input
              name="password"
              type="password"
              placeholder={t('login.password_placeholder')}
              label={t('login.password_label')}
              required={!sendEmail}
            />
            <div className="flex flex-col items-end w-full gap-y-1 sm:flex-row-reverse sm:items-center sm:justify-between">
              {!sendEmail ? toggleButton : null}
              <Text as="p" size="xs" type="body" theme={'default'} className="">
                <a href="/reset_password" className="underline">
                  <Text as="span" size="xs" type="body" weight="bold" theme={'default'}>
                    {t('login.set_new_password')}
                  </Text>
                </a>
              </Text>
            </div>
          </div>
        ) : null}
      </div>

      <Button disabled={isSubmitting} type="submit">
        {sendEmail ? actionLabel : t('login.login_button_label')}
      </Button>
    </div>
  );
};

export default LoginWithPassword;
